import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";

import { IconEdit } from "@lightspeed/flame/Icon/Edit";
import { Input } from "@lightspeed/flame/Input";
import { Button } from "@lightspeed/flame/Button";
import { setAlert, uiSelector } from "../../../redux/slices/uiSlice";
import Listbox from "../listbox/Listbox";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

const TdStyled = styled.td`
  padding: 0 8px !important;
  height: 0 !important;
  background: ${({ sectionBackground }) => sectionBackground || "transparent"};
  border-right: ${({ dividerAfter, dividerColor }) =>
    dividerAfter ? `3px solid ${dividerColor || "#88aef5"}` : "none"};
`;

const parseEditableValue = (rawValue) => {
  if (typeof rawValue !== "string") return rawValue;

  const trimmedValue = rawValue.trim();
  if (!trimmedValue) return "";

  return /^-?\d+(\.\d+)?$/.test(trimmedValue)
    ? Number(trimmedValue)
    : trimmedValue;
};

const Row = ({
  item,
  editable,
  editableData,
  updateAction,
  rowKeys,
  headerData = [],
}) => {
  const [edit, editSet] = useState(false);
  const [inputValues, inputValuesSet] = useState({});
  const form = useForm({
    defaultValues: item,
  });

  const { register, handleSubmit, setValue, reset, watch } = form;
  const dispatch = useDispatch();
  const { theme } = useSelector(uiSelector);

  useEffect(() => {
    reset(item);
    inputValuesSet({});
  }, [item, reset]);

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: 36,
      width: "100%",
      backgroundColor: theme === "flame" ? "#fff" : "#333",
      borderColor: theme === "flame" ? "#c7d3e3" : "#555",
      color: theme === "flame" ? "#333" : "#fff",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: theme === "flame" ? "#fff" : "#333",
      color: theme === "flame" ? "#333" : "#fff",
      width: "max-content",
      minWidth: "100%",
      maxWidth: "min(720px, calc(100vw - 32px))",
      zIndex: 200,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menuList: (base) => ({
      ...base,
      width: "max-content",
      minWidth: "100%",
      maxWidth: "min(720px, calc(100vw - 32px))",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused
        ? theme === "flame"
          ? "#eef4fd"
          : "#444"
        : theme === "flame"
        ? "#fff"
        : "#333",
      color: theme === "flame" ? "#333" : "#fff",
      whiteSpace: "nowrap",
    }),
    singleValue: (base) => ({
      ...base,
      color: theme === "flame" ? "#333" : "#fff",
    }),
    input: (base) => ({
      ...base,
      color: theme === "flame" ? "#333" : "#fff",
    }),
  };

  const onUpdate = (formData) => {
    let areSame = true;

    editable.forEach((editableKey) => {
      const oldValue = item[editableKey];
      const editedValue = formData[editableKey];

      if (String(oldValue ?? "") !== String(editedValue ?? "")) {
        areSame = false;
      }
    });

    if (areSame) {
      editSet(false);
      inputValuesSet({});
      return;
    }

    const newItem = { ...item, ...formData };

    editSet(false);
    inputValuesSet({});

    updateAction(newItem).then(() => {
      dispatch(
        setAlert({
          type: "success",
          title: "Success",
          text: "Task completed successfuly",
        })
      );

      setTimeout(() => {
        dispatch(
          setAlert({
            type: "",
            title: "",
            text: "",
          })
        );
      }, 3000);
    });
  };

  const onEdit = () => {
    editable.forEach((key) => {
      setValue(key, item[key] ?? "");
    });

    inputValuesSet({});
    editSet(true);
  };

  const wrapOnUpdate = handleSubmit(onUpdate);

  return (
    <FormProvider {...form}>
      <tr
        onDoubleClick={onEdit}
        className={`${theme === "flame" ? "" : "dark-table-row"}`}
      >
        {rowKeys.map((keyName, index) => {
          const columnMeta = headerData[index] || {};
          const cellProps = {
            sectionBackground: columnMeta.sectionBackground,
            dividerAfter: columnMeta.dividerAfter,
            dividerColor: columnMeta.dividerColor,
          };

          if (editable.includes(keyName) && edit) {
            if (editableData && editableData[keyName]?.type === "listBox") {
              const config = editableData[keyName];

              return (
                <TdStyled key={keyName} {...cellProps}>
                  <Listbox
                    onInputKeyDown={(e) => {
                      if (e.key === "Enter") {
                        wrapOnUpdate();
                      }
                    }}
                    items={config.items || []}
                    name={keyName}
                    inputType={config.inputType || "string"}
                  />
                </TdStyled>
              );
            }

            if (editableData && editableData[keyName]?.type === "dropdown") {
              const config = editableData[keyName];
              const currentValue = watch(keyName);
              const effectiveValue =
                currentValue !== undefined && currentValue !== null
                  ? currentValue
                  : item[keyName] ?? "";
              const baseItems = config.items || [];
              const hasCurrentOption = baseItems.some(
                (option) => String(option.value) === String(effectiveValue)
              );
              const fallbackOption =
                effectiveValue !== "" && effectiveValue != null && !hasCurrentOption
                  ? [
                      {
                        label: String(item[keyName] ?? effectiveValue),
                        value: effectiveValue,
                      },
                    ]
                  : [];
              const options = [...fallbackOption, ...baseItems];
              const selectedOption = options.find(
                (option) => String(option.value) === String(effectiveValue)
              ) || null;
              const SelectComponent = config.allowCreate ? CreatableSelect : Select;
              const inputValue = inputValues[keyName] ?? "";

              return (
                <TdStyled key={keyName} {...cellProps}>
                  <SelectComponent
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    options={options}
                    value={inputValue ? null : selectedOption}
                    inputValue={inputValue}
                    onInputChange={(nextValue, meta) => {
                      if (meta.action === "input-change") {
                        inputValuesSet((old) => ({ ...old, [keyName]: nextValue }));

                        if (config.allowCreate) {
                          setValue(keyName, parseEditableValue(nextValue), {
                            shouldDirty: true,
                          });
                        }
                      }

                      if (meta.action === "menu-close" || meta.action === "set-value") {
                        inputValuesSet((old) => ({ ...old, [keyName]: "" }));
                      }
                    }}
                    onChange={(option) => {
                      inputValuesSet((old) => ({ ...old, [keyName]: "" }));
                      setValue(keyName, option?.value ?? "", {
                        shouldDirty: true,
                      });
                    }}
                    onCreateOption={
                      config.allowCreate
                        ? (rawInputValue) => {
                            const parsedValue = parseEditableValue(rawInputValue);
                            inputValuesSet((old) => ({ ...old, [keyName]: "" }));
                            setValue(keyName, parsedValue, {
                              shouldDirty: true,
                            });
                          }
                        : undefined
                    }
                    isClearable
                    placeholder={config.placeholder || "Select option"}
                    noOptionsMessage={() => "No options available"}
                    formatCreateLabel={
                      config.allowCreate ? (nextValue) => nextValue : undefined
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        wrapOnUpdate();
                      }
                    }}
                  />
                </TdStyled>
              );
            }

            return (
              <TdStyled key={keyName} {...cellProps}>
                <Input
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      wrapOnUpdate();
                    }
                  }}
                  type={typeof item[keyName] === "number" ? "number" : "text"}
                  {...register(keyName, {
                    valueAsNumber:
                      typeof item[keyName] === "number" ? true : false,
                  })}
                />
              </TdStyled>
            );
          }

          return (
            <TdStyled key={keyName} {...cellProps}>
              {editableData?.[keyName]?.formatDisplayValue
                ? editableData[keyName].formatDisplayValue(item[keyName], item)
                : item[keyName]}
            </TdStyled>
          );
        })}

        {edit ? (
          <TdStyled
            sectionBackground={headerData[rowKeys.length]?.sectionBackground}
            dividerAfter={headerData[rowKeys.length]?.dividerAfter}
            dividerColor={headerData[rowKeys.length]?.dividerColor}
          >
            <Button onClick={wrapOnUpdate}>Save</Button>
            <Button
              onClick={() => {
                reset(item);
                inputValuesSet({});
                editSet(false);
              }}
            >
              Cancel
            </Button>
          </TdStyled>
        ) : (
          <TdStyled
            sectionBackground={headerData[rowKeys.length]?.sectionBackground}
            dividerAfter={headerData[rowKeys.length]?.dividerAfter}
            dividerColor={headerData[rowKeys.length]?.dividerColor}
          >
            <Button onClick={onEdit}>
              <IconEdit baseColor={theme === "flame" ? "#333" : "#fff"} />
            </Button>
          </TdStyled>
        )}
      </tr>
    </FormProvider>
  );
};

export default Row;
